import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { IdCard, AlertTriangle, Clock, MessageCircle, ArrowRight, Plus, Search, Users, CircleX, LayoutGrid, List as ListIcon, UserPlus, Sparkles } from "lucide-react";
import { endpoints } from "../lib/api";
import { useAuth } from "../lib/auth";
import { openWhatsApp, templates } from "../lib/wa";
import WaButton from "../components/WaButton";
import { MemberRiskBadge } from "../components/ScoringBadges";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";
import CsvImportDialog from "../components/CsvImportDialog";
import CsvExportButton from "../components/CsvExportButton";
import OnboardingModal from "../components/OnboardingModal";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const TABS = [
  { key: "all", label: "All", icon: Users },
  { key: "ready", label: "Ready to Onboard", icon: UserPlus },
  { key: "active", label: "Active", icon: IdCard },
  { key: "expiring", label: "Expiring soon", icon: Clock },
  { key: "atrisk", label: "At-risk", icon: AlertTriangle },
  { key: "expired", label: "Expired", icon: CircleX },
];

export default function Members() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", plan: "Monthly", plan_amount: 2000, duration_days: 30 });
  const [query, setQuery] = useState("");
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "all";
  const setTab = (k) => setParams(k === "all" ? {} : { tab: k }, { replace: true });
  const [layout, setLayout] = useState(() => {
    try { return localStorage.getItem("members_layout") || "list"; } catch { return "list"; }
  });
  useEffect(() => { try { localStorage.setItem("members_layout", layout); } catch {} }, [layout]);

  // Onboarding modal state
  const [onboardingMember, setOnboardingMember] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await endpoints.listMembers();
      setMembers(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // legacy hash support - /members#at-risk / #expiring
  useEffect(() => {
    const h = window.location.hash.replace("#", "");
    if (h === "at-risk") setTab("atrisk");
    else if (h === "expiring") setTab("expiring");
    else if (h === "active") setTab("active");
    else if (h === "expired") setTab("expired");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buckets = useMemo(() => {
    const now = new Date();
    const in7 = new Date(now); in7.setDate(in7.getDate() + 7);
    const riskCutoff = new Date(now); riskCutoff.setDate(riskCutoff.getDate() - 10);
    const active = [], expiring = [], atRisk = [], expired = [], ready = [];
    members.forEach(m => {
      // Ready-to-Onboard members are excluded from the activity buckets
      // so the dashboard counts (active/expiring/atrisk) reflect actually
      // operational members only.
      if (m.onboarding_stage === "ready_to_onboard") {
        ready.push(m);
        return;
      }
      const exp = new Date(m.expiry_date);
      if (exp < now) { expired.push(m); return; }
      if (exp <= in7) expiring.push(m);
      else active.push(m);
      const lv = m.last_visit ? new Date(m.last_visit) : null;
      if (!lv || lv < riskCutoff) atRisk.push(m);
    });
    return { all: members.filter(m => m.onboarding_stage !== "ready_to_onboard"), active, expiring, atrisk: atRisk, expired, ready };
  }, [members]);

  const counts = {
    all: buckets.all.length,
    ready: buckets.ready.length,
    active: buckets.active.length,
    expiring: buckets.expiring.length,
    atrisk: buckets.atrisk.length,
    expired: buckets.expired.length,
  };

  const visible = useMemo(() => {
    const src = buckets[tab] || [];
    const q = query.trim().toLowerCase();
    if (!q) return src;
    return src.filter(m =>
      (m.name || "").toLowerCase().includes(q) ||
      (m.phone || "").toLowerCase().includes(q) ||
      (m.email || "").toLowerCase().includes(q)
    );
  }, [buckets, tab, query]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) { toast.error("Name and phone required"); return; }
    try {
      await endpoints.createMember({
        ...form,
        plan_amount: parseFloat(form.plan_amount) || 0,
        duration_days: parseInt(form.duration_days) || 30,
      });
      toast.success("Added to Ready to Onboard", {
        description: "Capture personal & emergency details to activate the member.",
      });
      setOpen(false);
      setForm({ name: "", phone: "", plan: "Monthly", plan_amount: 2000, duration_days: 30 });
      // Switch view to ready tab so the user sees the new card immediately.
      setTab("ready");
      load();
    } catch {
      toast.error("Failed to add");
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-5xl mx-auto">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground/70 mb-2">Members</p>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-foreground" data-testid="members-title">
            Members
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Active roster, renewals and retention in one view.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <CsvExportButton kind="member" gymName={user?.gym_name} />
          <CsvImportDialog kind="member" onImported={load} />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button
                data-testid="add-member-btn"
                className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-medium px-4 py-2.5 rounded-lg transition-colors"
              >
                <Plus className="w-4 h-4" /> Add member
              </button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display">New member</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-4">
                <div><Label>Name</Label><Input data-testid="member-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
                <div><Label>Phone</Label><Input data-testid="member-phone-input" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+91 98765 43210" /></div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>Plan</Label><Input value={form.plan} onChange={e => setForm({ ...form, plan: e.target.value })} /></div>
                  <div><Label>Amount (₹)</Label><Input type="number" value={form.plan_amount} onChange={e => setForm({ ...form, plan_amount: e.target.value })} /></div>
                  <div><Label>Days</Label><Input type="number" value={form.duration_days} onChange={e => setForm({ ...form, duration_days: e.target.value })} /></div>
                </div>
                <DialogFooter>
                  <button type="submit" className="w-full bg-primary text-white font-medium py-2.5 rounded-lg hover:bg-primary/90 transition-colors">
                    Add member
                  </button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      {/* Search + Tabs */}
      <div className="mb-5 space-y-4">
        <div className="relative max-w-md" data-testid="members-search-wrapper">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/70 pointer-events-none" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, phone or email…"
            data-testid="members-search-input"
            className="w-full pl-9 pr-9 py-2.5 text-sm bg-card border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/40 placeholder:text-muted-foreground/60"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              data-testid="members-search-clear"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-muted-foreground/70 hover:text-foreground hover:bg-muted transition-colors"
              aria-label="Clear search"
            >
              <CircleX className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-1 overflow-x-auto bg-muted/30 p-1 rounded-lg w-fit" data-testid="members-tabs">
          {TABS.map(({ key, label, icon: Icon }) => {
            const active = tab === key;
            const c = counts[key] ?? 0;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setTab(key)}
                data-testid={`members-tab-${key}`}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md whitespace-nowrap transition-colors ${
                  active ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
                <span className={`ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                  active ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground/80"
                }`}>
                  {c}
                </span>
              </button>
            );
          })}
        </div>

        {/* Layout toggle */}
        <div className="flex items-center gap-0 border border-border bg-card rounded-lg overflow-hidden w-fit" data-testid="members-layout-toggle">
          <button
            type="button"
            data-testid="members-layout-list"
            onClick={() => setLayout("list")}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${
              layout === "list" ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground"
            }`}
            aria-pressed={layout === "list"}
            title="List view"
          >
            <ListIcon className="w-3.5 h-3.5" /> List
          </button>
          <button
            type="button"
            data-testid="members-layout-grid"
            onClick={() => setLayout("grid")}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border-l border-border transition-colors ${
              layout === "grid" ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground"
            }`}
            aria-pressed={layout === "grid"}
            title="Grid view"
          >
            <LayoutGrid className="w-3.5 h-3.5" /> Grid
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : visible.length === 0 ? (
        <div className="text-sm text-muted-foreground/70 border border-dashed border-border rounded-xl p-10 text-center" data-testid="members-empty-state">
          {query ? `No members match "${query}"` : emptyCopyFor(tab)}
        </div>
      ) : tab === "ready" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="ready-to-onboard-grid">
          {visible.map(m => (
            <ReadyToOnboardCard
              key={m.id}
              m={m}
              onStartOnboarding={() => setOnboardingMember(m)}
            />
          ))}
        </div>
      ) : layout === "grid" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="members-grid">
          {visible.map(m => <MemberCard key={m.id} m={m} />)}
        </div>
      ) : (
        <div className="border border-border rounded-xl divide-y divide-border/60 bg-card overflow-hidden" data-testid="members-list">
          {visible.map(m => <MemberRow key={m.id} m={m} />)}
        </div>
      )}

      <OnboardingModal
        member={onboardingMember}
        open={!!onboardingMember}
        onClose={() => setOnboardingMember(null)}
        onChanged={load}
      />
    </div>
  );
}

function emptyCopyFor(tab) {
  if (tab === "expiring") return "No renewals due in the next 7 days.";
  if (tab === "atrisk") return "No at-risk members. Great retention! 💪";
  if (tab === "expired") return "No expired members.";
  if (tab === "active") return "No active members yet.";
  if (tab === "ready") return "Nothing pending onboarding. Convert a lead to start the flow.";
  return "No members yet. Add your first one or import a CSV.";
}

function ReadyToOnboardCard({ m, onStartOnboarding }) {
  const created = m.created_at ? new Date(m.created_at) : null;
  const ageHrs = created ? Math.max(0, Math.floor((new Date() - created) / 3600000)) : null;
  const ageLabel = ageHrs == null ? ""
    : ageHrs < 1 ? "Just converted"
    : ageHrs < 24 ? `${ageHrs}h ago`
    : `${Math.floor(ageHrs / 24)}d ago`;

  const checklist = [
    { label: "Basics", done: !!(m.dob && m.gender) },
    { label: "Emergency contact", done: !!(m.emergency_contact_name && m.emergency_contact_phone) },
    { label: "Membership", done: !!(m.plan && m.expiry_date) },
    { label: "Waiver", done: m.waiver_status === "signed" },
    { label: "Payment", done: (m.payment_status === "paid") },
  ];
  const completed = checklist.filter(c => c.done).length;
  const pct = Math.round((completed / checklist.length) * 100);

  return (
    <div data-testid={`ready-card-${m.id}`} className="border border-border rounded-xl p-4 bg-card hover:border-primary/40 transition-colors">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="min-w-0 flex-1">
          <p className="font-display text-base font-bold text-foreground truncate">{m.name}</p>
          <p className="text-[11px] text-muted-foreground/85">{m.phone} · {m.plan}</p>
        </div>
        <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-md bg-amber-100 text-amber-700 border border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-900/50 shrink-0">
          {ageLabel || "New"}
        </span>
      </div>

      {/* Checklist */}
      <ul className="space-y-1.5 mb-4" data-testid={`ready-checklist-${m.id}`}>
        {checklist.map(c => (
          <li key={c.label} className="flex items-center gap-2 text-xs">
            <span className={`w-3.5 h-3.5 rounded-full flex items-center justify-center shrink-0 ${
              c.done ? "bg-emerald-500 text-white" : "border border-border bg-muted/40"
            }`}>
              {c.done && <svg viewBox="0 0 12 12" className="w-2 h-2"><path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" fill="none" /></svg>}
            </span>
            <span className={c.done ? "text-foreground" : "text-muted-foreground/85"}>{c.label}</span>
          </li>
        ))}
      </ul>

      {/* Progress */}
      <div className="flex items-center justify-between text-[10px] uppercase tracking-wider font-semibold text-muted-foreground/80 mb-1.5">
        <span>{completed}/{checklist.length} done</span>
        <span>{pct}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-muted overflow-hidden mb-4">
        <div className="h-full bg-primary transition-all duration-300" style={{ width: `${pct}%` }} />
      </div>

      <button
        type="button"
        onClick={onStartOnboarding}
        data-testid={`start-onboarding-${m.id}`}
        className="w-full bg-primary text-primary-foreground hover:opacity-90 text-sm font-semibold py-2 rounded-md inline-flex items-center justify-center gap-1.5"
      >
        <Sparkles className="w-3.5 h-3.5" /> Start onboarding
      </button>
    </div>
  );
}

function MemberRow({ m }) {
  const exp = new Date(m.expiry_date);
  const now = new Date();
  const days = Math.ceil((exp - now) / (1000 * 60 * 60 * 24));
  const lv = m.last_visit ? new Date(m.last_visit) : null;
  const lvDays = lv ? Math.floor((now - lv) / (1000 * 60 * 60 * 24)) : null;

  return (
    <div className="flex items-center gap-4 p-4 hover:bg-muted/30 transition-colors" data-testid={`member-row-${m.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-foreground truncate">{m.name}</span>
          <MemberRiskBadge member={m} size="sm" />
        </div>
        <div className="text-xs text-muted-foreground truncate">
          {m.plan} · {days >= 0 ? `${days}d left` : `expired ${-days}d ago`}
          {lvDays !== null ? ` · last visit ${lvDays}d ago` : " · no visits yet"}
          {m.phone ? ` · ${m.phone}` : ""}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <WaButton
          phone={m.phone}
          text={days <= 7 ? templates.renewal(m.name, Math.max(days, 1)) : templates.inactivity(m.name)}
          testId={`member-wa-${m.id}`}
          variant="primary"
          size="sm"
          title="Message on WhatsApp"
        >
          <MessageCircle className="w-3.5 h-3.5" />
        </WaButton>
        <Link to={`/members/${m.id}`} data-testid={`member-link-${m.id}`} className="p-1.5 text-muted-foreground/70 hover:text-foreground hover:bg-muted rounded-md">
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}

function MemberCard({ m }) {
  const exp = new Date(m.expiry_date);
  const now = new Date();
  const days = Math.ceil((exp - now) / (1000 * 60 * 60 * 24));
  const lv = m.last_visit ? new Date(m.last_visit) : null;
  const lvDays = lv ? Math.floor((now - lv) / (1000 * 60 * 60 * 24)) : null;
  const initials = (m.name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(s => s[0])
    .join("")
    .toUpperCase() || "M";

  // Attendance this month, computed from check-ins array.
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const attendanceThisMonth = Array.isArray(m.check_ins)
    ? m.check_ins.filter(c => new Date(c) >= monthStart).length
    : 0;

  let pillClass = "bg-growth/10 text-growth";
  let pillText = `${days}d left`;
  if (days < 0) { pillClass = "bg-risk/10 text-risk"; pillText = `Expired ${-days}d ago`; }
  else if (days <= 7) { pillClass = "bg-leads/10 text-leads"; pillText = `Renews in ${days}d`; }

  return (
    <div
      data-testid={`member-card-${m.id}`}
      className="group block border border-border rounded-2xl p-5 bg-card hover:border-foreground/15 hover:shadow-sm transition-all relative"
    >
      <Link to={`/members/${m.id}`} className="block">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-11 h-11 rounded-xl bg-growth/10 text-growth font-display font-semibold flex items-center justify-center shrink-0">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-display text-sm font-semibold text-foreground truncate">{m.name}</span>
              <MemberRiskBadge member={m} size="sm" />
            </div>
            <p className="text-xs text-muted-foreground truncate mt-0.5">{m.phone || "-"}</p>
          </div>
        </div>

        <div className="flex items-center gap-1.5 mb-4 flex-wrap">
          <span className="inline-flex items-center text-[10.5px] font-semibold px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
            {m.plan}
          </span>
          <span className={`inline-flex items-center text-[10.5px] font-semibold px-2 py-0.5 rounded-full ${pillClass}`}>
            {pillText}
          </span>
        </div>

        {/* Mini stat strip: attendance + last visit */}
        <div className="grid grid-cols-2 gap-3 pb-4 border-b border-border/60">
          <div>
            <p className="text-[10.5px] uppercase tracking-wider text-muted-foreground/70 font-semibold">This month</p>
            <p className="text-sm font-bold text-foreground mt-0.5">{attendanceThisMonth} <span className="text-xs font-normal text-muted-foreground">check-ins</span></p>
          </div>
          <div>
            <p className="text-[10.5px] uppercase tracking-wider text-muted-foreground/70 font-semibold">Last visit</p>
            <p className="text-sm font-bold text-foreground mt-0.5">{lvDays !== null ? (lvDays === 0 ? "Today" : `${lvDays}d ago`) : "Never"}</p>
          </div>
        </div>
      </Link>

      {/* Quick actions row */}
      <div className="flex items-center gap-2 pt-3.5">
        <WaButton
          phone={m.phone}
          text={days <= 7 ? templates.renewal(m.name, Math.max(days, 1)) : templates.inactivity(m.name)}
          testId={`member-card-wa-${m.id}`}
          variant="primary"
          size="sm"
          title="Message on WhatsApp"
        >
          <MessageCircle className="w-3.5 h-3.5" /> Message
        </WaButton>
        {days <= 7 && (
          <Link
            to={`/members/${m.id}#renew`}
            data-testid={`member-card-renew-${m.id}`}
            className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-md bg-growth/10 text-growth hover:bg-growth/20 transition-colors"
          >
            Renew
          </Link>
        )}
        <Link
          to={`/members/${m.id}`}
          data-testid={`member-card-open-${m.id}`}
          className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground"
        >
          Open <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
}

